import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ChangeDetectorRef,
  AfterViewInit,
  Input,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/store/state';
import { WorkspacesActions } from '../../../store/actions';
import { UserDto } from 'src/app/modules/login/services/user-api.service';
import { WorkspaceDto } from '../../../models/workspaces.model';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { constants } from 'src/app/core/constants/constants';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { AlertType } from 'src/app/shared/components/app-alert/app-alert.component';
import {
  selectCloseRenamingWorkspaceModal,
  selectWorkspaceRenaming,
} from '../../../store/selectors';

@UntilDestroy()
@Component({
  selector: 'rename-workspace-modal',
  styleUrl: './rename-workspace-modal.component.scss',
  templateUrl: './rename-workspace-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenameWorkspaceModalComponent implements OnInit, AfterViewInit {
  @Input() workspace!: WorkspaceDto;
  public onClose: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup | undefined;
  public title: string = this.translocoService.translate(`workspaces.renameWorkspace`);
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public alertTypes = AlertType;
  public constants = constants;

  public user$: Observable<UserDto | null> | undefined;
  public currentUser: UserDto | null | undefined;
  public isWorkspaceRenaming$ = this.store$.select(selectWorkspaceRenaming);

  public maxFileCountError: boolean = false;
  public files: NgxFileDropEntry[] = [];
  public wrongFileType: boolean = false;
  public wrongFileTypeErrorMessage: string = '';
  public canWrite = false;

  constructor(
    private store$: Store<AppState>,
    private bsModalRef: BsModalRef,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly permissionService: PermissionService,
  ) {}

  ngAfterViewInit(): void {
    this.store$
      .select(selectCloseRenamingWorkspaceModal)
      .pipe(untilDestroyed(this))
      .subscribe((close) => {
        if (close) {
          this.bsModalRef.hide();
          this.store$.dispatch(WorkspacesActions.clearRenameWorkspaceCancelled());
        }
      });
  }

  public ngOnInit(): void {
    this.initForm();
    this.permissionService
      .canWrite$(PermissionResource.Workspaces)
      .pipe(untilDestroyed(this))
      .subscribe((canWrite) => {
        this.canWrite = canWrite;
        this.syncFormAccess();
      });
  }

  initForm() {
    this.form = this.fb.group({
      name: [this.workspace.name, { validators: [Validators.required] }],
    });

    this.syncFormAccess();
    this.cdr.markForCheck();
  }

  public onConfirm(): void {
    if (!this.canWrite) {
      return;
    }

    const model = this.form?.getRawValue() as WorkspaceDto;

    this.store$.dispatch(
      WorkspacesActions.renameWorkspace({ name: model.name, id: this.workspace.id }),
    );
  }

  public onCancel(): void {
    const pristine = !!this.form?.pristine;
    this.store$.dispatch(WorkspacesActions.renameWorkspaceCancel({ pristine }));
  }

  private syncFormAccess(): void {
    if (!this.form) {
      return;
    }

    if (this.canWrite) {
      this.form.enable({ emitEvent: false });
      return;
    }

    this.form.disable({ emitEvent: false });
  }
}
